import React, {ReactNode, useRef, useState} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Apla} from "@pg-design/apla-module";
import {borderRadius, calculateRemSize as crs, fadeInAnimation, p} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons";
import {ChevronDownIcon} from "@pg-design/icons";
import {InputWrapper, inputWrapperHorizontalPaddingSize} from "@pg-design/inputs";
import {Text} from "@pg-design/text";
import {useUserDevice} from "@pg-mono/user-device";

import {isKeyDownFromButton} from "../../../search/components/filters/SearchRangeFilter";

interface IProps {
    label: string | ReactNode;
    children: (props: IChildrenProps) => React.JSX.Element;
    disabled?: boolean;
    isEmpty?: boolean;
    rightDropdown?: boolean;
    onClear?: () => void;
    // Nazwa przycisku czyszczenia; etykieta może być węzłem React, więc nie da się jej użyć jako nazwy
    clearAriaLabel?: string;
    aplaCss?: Interpolation<Theme>;
    onDropdownOpen?: () => void;
    onDropdownClose?: (reason?: DropdownCloseReason) => void;
    className?: string;
}

// „clear”: dropdown zamyka akcja Wyczyść, więc właściciel nie powinien zatwierdzać wersji roboczej (np. sortowania)
export type DropdownCloseReason = "clear";

interface IChildrenProps {
    close: (reason?: DropdownCloseReason) => void;
}

export function FakeSelectWithModal(props: IProps) {
    const {label, disabled, isEmpty, rightDropdown, className, children, onClear, clearAriaLabel = "Wyczyść filtr"} = props;

    const {isMobile} = useUserDevice();
    const [isOpened, setIsOpened] = useState(false);
    const labelButtonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleDropdownOpen = () => {
        setIsOpened(true);
        props.onDropdownOpen?.();
    };

    const handleDropdownClose = (reason?: DropdownCloseReason) => {
        // fokus w środku dropdownu (np. na „Wybierz”) zniknąłby razem z nim i wylądował na body; kliknięcie poza dropdownem fokusu nie dotyczy
        const shouldRestoreFocus = !!dropdownRef.current?.contains(document.activeElement);

        setIsOpened(false);
        props.onDropdownClose?.(reason);

        if (shouldRestoreFocus) {
            labelButtonRef.current?.focus();
        }
    };

    const handlers = {
        keyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Escape") {
                handleDropdownClose();
            }

            // Enter na buttonie (chip, „×”, „Wybierz”) tylko go klika; zamknięcie dropdownu przed clickiem odbierałoby „Wybierz” zatwierdzenie
            if (e.key === "Enter" && !isKeyDownFromButton(e)) {
                handleDropdownClose();
            }
        },
        upArrowClick: (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();

            handleDropdownClose();
        },
        open: () => {
            handleDropdownOpen();
        },
        close: (reason?: DropdownCloseReason) => {
            handleDropdownClose(reason);
        },
        clear: (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (onClear) {
                onClear();
            }

            // przycisk „×” znika razem z wartością, więc fokus wraca na trwały przycisk chipa
            labelButtonRef.current?.focus();
        }
    };

    return (
        <>
            <InputWrapper
                className={className}
                css={fieldWrap}
                inputWrapCss={inputWrap}
                onClick={(e) => {
                    if (!disabled && isOpened) {
                        handlers.upArrowClick(e);
                    }

                    if (!disabled && !isOpened) {
                        handlers.open();
                    }
                }}
            >
                <div css={filterHolder} onKeyDown={handlers.keyDown}>
                    {/* Otwieranie i zamykanie obsługuje onClick na InputWrapper: klik w przycisk etykiety do niego bąbelkuje */}
                    <div css={filterLabel}>
                        <button
                            type="button"
                            ref={labelButtonRef}
                            css={[buttonReset, labelButton]}
                            aria-expanded={isOpened}
                            aria-disabled={disabled || undefined}
                        >
                            <Text variant="body_copy_2" as="span" css={filterLabelText}>
                                {label}
                            </Text>

                            {isEmpty && <SearchDropdownArrow isDown={!isOpened} />}
                        </button>

                        {!isEmpty && (
                            <button type="button" css={[buttonReset, clearButton]} aria-label={clearAriaLabel} onClick={handlers.clear}>
                                <CloseIcon size="1.4" />
                            </button>
                        )}
                    </div>
                    {(isOpened || isMobile) && (
                        <ChildHolder
                            ref={dropdownRef}
                            rightDropdown={rightDropdown}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            {children({
                                close: handlers.close
                            })}
                        </ChildHolder>
                    )}
                </div>
            </InputWrapper>

            {isOpened && (
                <Apla
                    applyOn={isOpened}
                    css={[aplaStyle, props.aplaCss]}
                    onClick={() => {
                        handlers.close();
                    }}
                />
            )}
        </>
    );
}

/*
    Sub-components
 */

interface ISearchDropdownArrowProps {
    isDown: boolean;
}

export const SearchDropdownArrow = (props: ISearchDropdownArrowProps) => {
    return (
        <div css={!props.isDown && rotatedStyle}>
            <ChevronDownIcon size="2" />
        </div>
    );
};

const rotatedStyle = css`
    transform: rotate(180deg);
`;

/*
    Styles
 */
const fieldWrap = css`
    display: flex;
    height: ${crs(6)};
    ${p(0, inputWrapperHorizontalPaddingSize)};
    align-items: center;
    cursor: pointer;
`;

const inputWrap = css`
    min-width: 0;
`;

const filterHolder = (theme: Theme) => css`
    position: relative;
    width: 100%;

    @media (max-width: calc(${theme.breakpoints.md} - 1px)) {
        padding: 0;

        &:focus {
            outline: none;
            box-shadow: none;
        }
    }
`;

const filterLabel = (theme: Theme) => css`
    display: none;

    &:focus {
        outline: none;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }
`;

const buttonReset = css`
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid var(--colors-secondary);
        outline-offset: 2px;
    }
`;

const labelButton = css`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    text-align: left;
`;

const clearButton = css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    padding: 0;
`;

const filterLabelText = css`
    display: block;
    padding-right: 1rem;
    min-width: 0;
`;

interface IChildHolderProps {
    rightDropdown?: boolean;
}

const ChildHolder = styled.div<IChildHolderProps>`
    ${({theme, rightDropdown}) => css`
        background: #fff;
        padding: 1rem 2rem;

        .range-input-lower,
        .range-input-upper {
            margin-bottom: 10px;
        }

        @media (min-width: ${theme.breakpoints.md}) {
            ${fadeInAnimation("0.2s")};

            position: absolute;
            top: 6rem;
            z-index: 1001;
            right: ${rightDropdown ? 0 : "unset"};
            left: ${rightDropdown ? "unset" : 0};

            padding: 2rem 3rem;
            ${borderRadius(4)};
            min-width: 36rem;
            max-height: 60rem;
        }
    `}
`;

const aplaStyle = css`
    position: fixed;
    inset: 0;
    z-index: 50;
    ${borderRadius(2)};
    overflow: hidden;
`;
import React, {ReactNode, useRef, useState} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Apla} from "@pg-design/apla-module";
import {borderRadius, calculateRemSize as crs, fadeInAnimation, p} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons";
import {ChevronDownIcon} from "@pg-design/icons";
import {InputWrapper, inputWrapperHorizontalPaddingSize} from "@pg-design/inputs";
import {Text} from "@pg-design/text";
import {useUserDevice} from "@pg-mono/user-device";

import {isKeyDownFromButton} from "../../../search/components/filters/SearchRangeFilter";

interface IProps {
    label: string | ReactNode;
    children: (props: IChildrenProps) => React.JSX.Element;
    disabled?: boolean;
    isEmpty?: boolean;
    rightDropdown?: boolean;
    onClear?: () => void;
    // Nazwa przycisku czyszczenia; etykieta może być węzłem React, więc nie da się jej użyć jako nazwy
    clearAriaLabel?: string;
    aplaCss?: Interpolation<Theme>;
    onDropdownOpen?: () => void;
    onDropdownClose?: (reason?: DropdownCloseReason) => void;
    className?: string;
}

// „clear”: dropdown zamyka akcja Wyczyść, więc właściciel nie powinien zatwierdzać wersji roboczej (np. sortowania)
export type DropdownCloseReason = "clear";

interface IChildrenProps {
    close: (reason?: DropdownCloseReason) => void;
}

export function FakeSelectWithModal(props: IProps) {
    const {label, disabled, isEmpty, rightDropdown, className, children, onClear, clearAriaLabel = "Wyczyść filtr"} = props;

    const {isMobile} = useUserDevice();
    const [isOpened, setIsOpened] = useState(false);
    const labelButtonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleDropdownOpen = () => {
        setIsOpened(true);
        props.onDropdownOpen?.();
    };

    const handleDropdownClose = (reason?: DropdownCloseReason) => {
        // fokus w środku dropdownu (np. na „Wybierz”) zniknąłby razem z nim i wylądował na body; kliknięcie poza dropdownem fokusu nie dotyczy
        const shouldRestoreFocus = !!dropdownRef.current?.contains(document.activeElement);

        setIsOpened(false);
        props.onDropdownClose?.(reason);

        if (shouldRestoreFocus) {
            labelButtonRef.current?.focus();
        }
    };

    const handlers = {
        keyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Escape") {
                handleDropdownClose();
            }

            // Enter na buttonie (chip, „×”, „Wybierz”) tylko go klika; zamknięcie dropdownu przed clickiem odbierałoby „Wybierz” zatwierdzenie
            if (e.key === "Enter" && !isKeyDownFromButton(e)) {
                handleDropdownClose();
            }
        },
        upArrowClick: (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();

            handleDropdownClose();
        },
        open: () => {
            handleDropdownOpen();
        },
        close: (reason?: DropdownCloseReason) => {
            handleDropdownClose(reason);
        },
        clear: (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (onClear) {
                onClear();
            }

            // przycisk „×” znika razem z wartością, więc fokus wraca na trwały przycisk chipa
            labelButtonRef.current?.focus();
        }
    };

    return (
        <>
            <InputWrapper
                className={className}
                css={fieldWrap}
                inputWrapCss={inputWrap}
                onClick={(e) => {
                    if (!disabled && isOpened) {
                        handlers.upArrowClick(e);
                    }

                    if (!disabled && !isOpened) {
                        handlers.open();
                    }
                }}
            >
                <div css={filterHolder} onKeyDown={handlers.keyDown}>
                    {/* Otwieranie i zamykanie obsługuje onClick na InputWrapper: klik w przycisk etykiety do niego bąbelkuje */}
                    <div css={filterLabel}>
                        <button
                            type="button"
                            ref={labelButtonRef}
                            css={[buttonReset, labelButton]}
                            aria-expanded={isOpened}
                            aria-disabled={disabled || undefined}
                        >
                            <Text variant="body_copy_2" as="span" css={filterLabelText}>
                                {label}
                            </Text>

                            {isEmpty && <SearchDropdownArrow isDown={!isOpened} />}
                        </button>

                        {!isEmpty && (
                            <button type="button" css={[buttonReset, clearButton]} aria-label={clearAriaLabel} onClick={handlers.clear}>
                                <CloseIcon size="1.4" />
                            </button>
                        )}
                    </div>
                    {(isOpened || isMobile) && (
                        <ChildHolder
                            ref={dropdownRef}
                            rightDropdown={rightDropdown}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            {children({
                                close: handlers.close
                            })}
                        </ChildHolder>
                    )}
                </div>
            </InputWrapper>

            {isOpened && (
                <Apla
                    applyOn={isOpened}
                    css={[aplaStyle, props.aplaCss]}
                    onClick={() => {
                        handlers.close();
                    }}
                />
            )}
        </>
    );
}

/*
    Sub-components
 */

interface ISearchDropdownArrowProps {
    isDown: boolean;
}

export const SearchDropdownArrow = (props: ISearchDropdownArrowProps) => {
    return (
        <div css={!props.isDown && rotatedStyle}>
            <ChevronDownIcon size="2" />
        </div>
    );
};

const rotatedStyle = css`
    transform: rotate(180deg);
`;

/*
    Styles
 */
const fieldWrap = css`
    display: flex;
    height: ${crs(6)};
    ${p(0, inputWrapperHorizontalPaddingSize)};
    align-items: center;
    cursor: pointer;
`;

const inputWrap = css`
    min-width: 0;
`;

const filterHolder = (theme: Theme) => css`
    position: relative;
    width: 100%;

    @media (max-width: calc(${theme.breakpoints.md} - 1px)) {
        padding: 0;

        &:focus {
            outline: none;
            box-shadow: none;
        }
    }
`;

const filterLabel = (theme: Theme) => css`
    display: none;

    &:focus {
        outline: none;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }
`;

const buttonReset = css`
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid var(--colors-secondary);
        outline-offset: 2px;
    }
`;

const labelButton = css`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    text-align: left;
`;

const clearButton = css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    padding: 0;
`;

const filterLabelText = css`
    display: block;
    padding-right: 1rem;
    min-width: 0;
`;

interface IChildHolderProps {
    rightDropdown?: boolean;
}

const ChildHolder = styled.div<IChildHolderProps>`
    ${({theme, rightDropdown}) => css`
        background: #fff;
        padding: 1rem 2rem;

        .range-input-lower,
        .range-input-upper {
            margin-bottom: 10px;
        }

        @media (min-width: ${theme.breakpoints.md}) {
            ${fadeInAnimation("0.2s")};

            position: absolute;
            top: 6rem;
            z-index: 1001;
            right: ${rightDropdown ? 0 : "unset"};
            left: ${rightDropdown ? "unset" : 0};

            padding: 2rem 3rem;
            ${borderRadius(4)};
            min-width: 36rem;
            max-height: 60rem;
        }
    `}
`;

const aplaStyle = css`
    position: fixed;
    inset: 0;
    z-index: 50;
    ${borderRadius(2)};
    overflow: hidden;
`;
